import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageCode } from 'src/app/_models';

@Injectable({
  providedIn: 'root',
})
export class OrganizationConfigService {
  private activeOrganization = new BehaviorSubject<string>(localStorage.getItem('organizationId')!);

  setActiveOrganization(organizationId: string) {
    this.activeOrganization.next(organizationId);
  }

  getActiveOrganization(): Observable<string> {
    return this.activeOrganization.asObservable();
  }

  getOrganizationConfig(org: string): OrganizationConfig {
    return organizationConfigs[org];
  }

  /**
   * Returns organization config for the given language. Use on the login page
   * so the membership link targets the correct country shop by active language,
   * not by the organization from the poll.
   */
  getOrganizationConfigByLanguage(lang: LanguageCode): OrganizationConfig {
    const orgKey = this.getOrganizationKeyByLanguage(lang);
    return this.getOrganizationConfig(orgKey);
  }

  private getOrganizationKeyByLanguage(lang: LanguageCode): string {
    switch (lang) {
      case LanguageCode.Danish:
        return 'VIKINGDK';
      case LanguageCode.Swedish:
        return 'VIKINGSE';
      case LanguageCode.Norwegian:
        return 'VIKINGNO';
      default:
        return 'VIKINGNO';
    }
  }
}

export interface OrganizationConfig {
  contactEmail: string;
  contactEmailSubjectChangeVehicleInfo: string;
  contactPhone: string;
  contactPhoneHref: string;
  faqUrl: string;
  webshopUrl?: string;
  privacyPolicyUrl: string; // Optional for DK and SE
  vikingKontrollUrl: string;
  membershipUrl: string;
  dob: string;
  helpFAQUrl: string;
  helpMembershipUrl: string;
  helpVehicleAssistanceUrl: string;
}

const organizationConfigs: Record<string, OrganizationConfig> = {
  VIKINGNO: {
    contactEmail: 'medlem@vikingredning.no',
    contactEmailSubjectChangeVehicleInfo: 'Ändre%20kjøretøyinformasjon',
    contactPhone: '22 08 60 10',
    contactPhoneHref: '+4722086010',
    faqUrl: 'https://www.vikingredning.no/veiledninger/temaer/vikingmedlemskap',
    webshopUrl: 'https://www.vikingredning.no/nettbutikk',
    privacyPolicyUrl: '/shop/privacy-policy/no',
    vikingKontrollUrl: 'https://www.vikingredning.no/kontroll',
    membershipUrl: 'https://www.vikingredning.no/medlem',
    dob: ' ååååmmdd (ex: 19801231)',
    helpFAQUrl: 'https://www.vikingredning.no/faq',
    helpVehicleAssistanceUrl: 'https://www.vikingredning.no/faq/temaer/veihjelp-bilberging',
    helpMembershipUrl: 'https://www.vikingredning.no/faq/temaer/medlemskap',
  },
  VIKINGSE: {
    contactEmail: 'medlem@vikingvagassistans.se',
    contactEmailSubjectChangeVehicleInfo: 'Ändra%20fordonsinformation',
    contactPhone: '0771-282828',
    contactPhoneHref: '+46771282828',
    faqUrl: 'https://www.vikingvagassistans.se/faq/amnen/medlemskap',
    webshopUrl: 'https://www.vikingvagassistans.se/webbutik',
    privacyPolicyUrl: '/shop/privacy-policy/se',
    vikingKontrollUrl: 'https://www.vikingvagassistans.se/kontroll',
    membershipUrl: 'https://www.vikingvagassistans.se/medlem',
    dob: '- ååååmmddxxxx (ex: 19801231xxxx)',
    helpFAQUrl: 'https://www.vikingvagassistans.se/faq',
    helpVehicleAssistanceUrl: 'https://www.vikingvagassistans.se/faq/amnen/vagassistans',
    helpMembershipUrl: 'https://www.vikingvagassistans.se/faq/amnen/medlemskap',
  },
  VIKINGDK: {
    contactEmail: 'medlemdk@vikingassistance.com',
    contactEmailSubjectChangeVehicleInfo: 'Ændre%20køretøjsinformation',
    contactPhone: '+45 44 20 00 30',
    contactPhoneHref: '+4544200030',
    faqUrl: 'https://www.vikingassistance.dk/faq',
    // webshopUrl: 'https://www.vikingassistance.dk/medlemskab',
    privacyPolicyUrl: 'https://www.vikingassistance.dk/privatlivspolitik',
    vikingKontrollUrl: 'https://www.vikingredning.dk/kontrol',
    membershipUrl: 'https://www.vikingassistance.dk/medlem',
    dob: '- ååååmmdd (ex: 19801231)',
    helpFAQUrl: 'https://www.vikingassistance.dk/faq',
    helpVehicleAssistanceUrl: 'https://www.vikingassistance.dk/faq/emner/vejhjaelp',
    helpMembershipUrl: 'https://www.vikingassistance.dk/faq/emner/medlemskab',
  },
};
